import * as React from 'react'
import { Button, mergeClasses, Text, Tooltip } from '@fluentui/react-components'
import { Link } from 'react-router-dom'
import {
    ArrowUndo16Filled,
    ArrowUndo16Regular,
    bundleIcon,
    Checkmark16Filled,
    Checkmark16Regular,
    CheckmarkCircle16Filled,
    CheckmarkCircle16Regular,
    DocumentArrowDownRegular,
    DocumentPdfRegular,
    Drafts16Filled,
    Drafts16Regular,
    History16Filled,
    MailAddRegular,
    New16Filled,
    New16Regular,
} from '@fluentui/react-icons'
import { InvoiceLineItemProps, useItemStyles } from './InvoiceOverview'
import { useTranslation } from 'react-i18next'
import { useOverviewStyles } from '../../BaseOverview/BaseOverview'
import { InvoiceStatusLabels } from '../../../components/TagPicker/InvoiceStatusPicker'
import AccountingSubmissionPreview from '../../../components/Preview/AccountingSubmissionPreview'
import { InvoiceStatus, InvoiceType } from '../../../config/finance/invoiceConfig'
import PersonAvatar from '../../../components/PersonAvatar'
import { DateFormats } from '../../../helpers/dateHelper'
import MoneyText from '../../../components/Text/Currency/MoneyText'
import InvoiceNoteDialog from '../../../components/Dialog/InvoiceNote/InvoiceNoteDialog'
import useAxios from '../../../hooks/useAxios'
import { useQueue } from '../../../hooks/useQueue'
import { stealthClick } from '../../../components/StealthLink'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationStatusData, DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import { InvoiceParams } from '../../../hooks/finance/invoice/useSubmitInvoiceDocumentGeneration'
import { QueueStatus } from '../../../hooks/useQueueStateHandler'
import { getDriveItemDownload } from '../../../helpers/documentHelper'
import useComposeInvoiceOutlookMessage from '../../../hooks/finance/invoice/useComposeInvoiceOutlookMessage'
import { isOfficeInitialized } from '../../../helpers/outlookHelper'
import { formatDecimals, parseNumber } from '../../../helpers/numberHelper'
import FormattedDateRange from '../../../components/Date/FormattedDateRange'
import { DocumentOpenType } from '../../../context/GlobalContext'
import { useSettings } from '../../../hooks/useSettings'
import useCurrencies from '../../../hooks/useCurrencies'
import useUnits from '../../../hooks/useUnits'
import InvoiceListItemTurnover from './InvoiceListItemTurnover'

const NewIcon = bundleIcon(New16Filled, New16Regular)
const DraftIcon = bundleIcon(Drafts16Filled, Drafts16Regular)
const FinalIcon = bundleIcon(Checkmark16Filled, Checkmark16Regular)
const SettledIcon = bundleIcon(ArrowUndo16Filled, ArrowUndo16Regular)
const CreditedIcon = bundleIcon(History16Filled, ArrowUndo16Regular)
const DefaultIcon = bundleIcon(CheckmarkCircle16Filled, CheckmarkCircle16Regular)

function getStatusIcon(status: InvoiceStatus) {
    switch (status) {
        case InvoiceStatus.New:
            return <NewIcon />
        case InvoiceStatus.Draft:
            return <DraftIcon />
        case InvoiceStatus.Final:
            return <FinalIcon />
        case InvoiceStatus.Settled:
            return <SettledIcon />
        case InvoiceStatus.Credited:
            return <CreditedIcon />
        default:
            return <DefaultIcon />
    }
}

function InvoiceListItemTitle(props: InvoiceLineItemProps) {
    const axios = useAxios()
    const composeOutlookMessage = useComposeInvoiceOutlookMessage()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const queue = useQueue()
    const { settings } = useSettings()

    const generatePdf = useSubmitDocumentGenerationRequest<InvoiceParams, DocumentGenerationStatusData>(DocumentGenerationType.Invoice)

    return (
        <div className={mergeClasses(overviewStyles.flexJustifyBetween, overviewStyles.minWidth0)}>
            <Text size={200} className={mergeClasses(overviewStyles.flexItemGrowOverflow, overviewStyles.minWidth0)}>
                {props.item.recipient || props.item.files
                    ? props.item.recipient?.name || `Gedeelde declaratie ${props.item.files?.[0]?.reference} - ${props.item.name}`
                    : undefined}
            </Text>
            <aside data-hover-actions={'visibility'} className={overviewStyles.flexGapXS}>
                {![InvoiceType.Split].includes(props.item.type) && !props.item.pdfId && (
                    <Tooltip relationship={'label'} content={translate('invoice.action.generatepdf')} withArrow>
                        <Button
                            aria-label={'hover'}
                            icon={<DocumentPdfRegular />}
                            appearance="secondary"
                            size={'small'}
                            disabled={queue.has(props.item.id)}
                            onClick={() => {
                                generatePdf({ invoiceId: props.item.id }, props.item.number, {
                                    [QueueStatus.Finished]: data => {
                                        props.onUpdateItem(props.item)
                                        stealthClick(
                                            {
                                                id: data.status.driveItem.id,
                                                item: data.status.driveItem,
                                                documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open,
                                            },
                                            axios,
                                        )
                                    },
                                })
                            }}
                        />
                    </Tooltip>
                )}
                {![InvoiceType.Split].includes(props.item.type) && props.item.pdfId && (
                    <Tooltip relationship={'label'} content={translate('invoice.action.downloadpdf')} withArrow>
                        <Button
                            aria-label={'hover'}
                            icon={<DocumentArrowDownRegular />}
                            appearance="secondary"
                            size={'small'}
                            disabled={queue.has(props.item.id)}
                            onClick={() =>
                                stealthClick({ id: props.item.pdfId, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open }, axios)
                            }
                        />
                    </Tooltip>
                )}
                {isOfficeInitialized() &&
                    ![InvoiceType.Split].includes(props.item.type) &&
                    props.item.pdfId &&
                    props.item.status === InvoiceStatus.Final && (
                        <Tooltip relationship={'label'} content={translate('invoice.action.sendemail')} withArrow>
                            <Button
                                aria-label={'hover'}
                                icon={<MailAddRegular />}
                                appearance="secondary"
                                size={'small'}
                                disabled={queue.has(props.item.id)}
                                onClick={() => {
                                    getDriveItemDownload(props.item.pdfId, axios)
                                        .then(pdf => composeOutlookMessage(props.item.id, props.item.recipient?.email || '', pdf))
                                        .catch(error => {
                                            console.log(error)
                                        })
                                }}
                            />
                        </Tooltip>
                    )}
            </aside>
        </div>
    )
}

export default function InvoiceListItem(props: InvoiceLineItemProps) {
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()

    const { getCurrency } = useCurrencies()
    const { t: translate } = useTranslation()
    const { units } = useUnits()

    const statusIcon = getStatusIcon(props.item.status)

    if (props.viewportNarrow) {
        return (
            <>
                <PersonAvatar name={props.item.fileOwner.fullName} color={'colorful'} size={24} />
                <div className={itemStyles.listInvoiceItemTitle}>
                    <InvoiceListItemTitle {...props} />
                    <Link className={overviewStyles.alignItemsEnd} to={`/invoice/${props.item.id}/edit`}>
                        {props.item.number}
                    </Link>
                    <InvoiceNoteDialog invoice={props.item} onSubmit={value => (props.item.note = value)} />
                </div>
                <div>
                    <Tooltip
                        relationship={'label'}
                        content={
                            <div>
                                {translate(InvoiceStatusLabels.get(props.item.status)?.name, {
                                    count: 1,
                                    currency: getCurrency(props.item.unsettledPrice?.currency)?.symbol || props.item.unsettledPrice?.currency,
                                    amount:
                                        formatDecimals(
                                            props.item.unsettledPrice?.total || 0,
                                            getCurrency(props.item.unsettledPrice?.currency)?.decimals || 2,
                                            'en',
                                            'nl-nl',
                                        ) || 0,
                                })}
                            </div>
                        }
                        withArrow
                    >
                        <Button icon={statusIcon} appearance={'transparent'} size={'small'} />
                    </Tooltip>
                    {units.length > 0 && ![InvoiceType.Split].includes(props.item.type) && (
                        <AccountingSubmissionPreview item={props.item} reset={() => props.onUpdateItem(props.item)} />
                    )}
                </div>
                <div className={itemStyles.listInvoiceItemNarrowBottomRow}>
                    <div>
                        <FormattedDateRange fromDate={props.item.date} toDate={props.item.dueDate} date={props.item.date} format={DateFormats.Full} />
                    </div>

                    <Link key={props.item.id} to={`/file/${props.item.files?.[0]?.id}/time`}>
                        <Text size={200}>
                            {props.item.files?.[0]?.reference} {props.item.files?.length > 1 ? `+${props.item.files?.length - 1}` : ''}
                        </Text>
                    </Link>
                    <InvoiceListItemTurnover {...props} />
                </div>
            </>
        )
    }

    return (
        <>
            <PersonAvatar name={props.item.fileOwner.fullName} color={'colorful'} size={24} />
            <Tooltip
                relationship={'label'}
                content={
                    <div>
                        {translate(InvoiceStatusLabels.get(props.item.status)?.name, {
                            count: 1,
                            currency: getCurrency(props.item.unsettledPrice?.currency)?.symbol || props.item.unsettledPrice?.currency,
                            amount:
                                formatDecimals(
                                    props.item.unsettledPrice?.total || 0,
                                    getCurrency(props.item.unsettledPrice?.currency)?.decimals || 2,
                                    'en',
                                    'nl-nl',
                                ) || 0,
                        })}
                    </div>
                }
                withArrow
            >
                <Button icon={statusIcon} appearance={'transparent'} size={'small'} />
            </Tooltip>
            {units.length > 0 && ![InvoiceType.Split].includes(props.item.type) ? (
                <AccountingSubmissionPreview item={props.item} reset={() => props.onUpdateItem(props.item)} />
            ) : (
                <div></div>
            )}
            <Link to={`/invoice/${props.item.id}/edit`}>{props.item.number}</Link>
            <div>
                <FormattedDateRange fromDate={props.item.date} toDate={props.item.dueDate} date={props.item.date} format={DateFormats.Full} />
            </div>
            <InvoiceListItemTitle {...props} />
            <InvoiceListItemTurnover {...props} />

            {!props.viewportMedium && (
                <MoneyText
                    value={{
                        currency: props.item.price.currency,
                        amount: props.item.price.vat
                            .map(vat => parseNumber(vat.value, 'en'))
                            .reduce((a, b) => a + b, 0)
                            .toString(),
                    }}
                    textProps={{
                        size: 300,
                    }}
                    align="right"
                />
            )}
            {!props.viewportMedium && (
                <MoneyText
                    value={{
                        currency: props.item.outstandingPrice?.currency || props.item.price.currency,
                        amount: props.item.outstandingPrice?.total || '0.00',
                    }}
                    textProps={{
                        size: 300,
                    }}
                    align="right"
                />
            )}

            <Tooltip relationship={'label'} content={props.item.files?.map(file => `${file.reference}: ${file.name}`).join(', ')} withArrow>
                <Link key={props.item.id} to={`/file/${props.item.files?.[0]?.id}/time`} className={overviewStyles.alignItemsEnd}>
                    <Text size={200}>
                        {props.item.files?.[0]?.reference} {props.item.files?.length > 1 ? `+${props.item.files?.length - 1}` : ''}
                    </Text>
                </Link>
            </Tooltip>
            <InvoiceNoteDialog invoice={props.item} onSubmit={value => (props.item.note = value)} />
        </>
    )
}
